import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormLabel, { type FormLabelProps } from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import { type ReactElement, type ReactNode, useMemo } from 'react';
import { type FieldValues, useFormState } from 'react-hook-form';
import { useDesignSystemEnabled } from '../theme/useDesignSystemEnabled';
import { RHFTextField } from './RHFTextField';

export type RHFTextFieldWithAdornmentProps<T extends FieldValues = Record<string, string>> = Omit<
  Parameters<typeof RHFTextField<T>>[0],
  'variant'
> & {
  dataTest?: string;
  errorsRequireInteraction?: boolean;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  formControlProps?: FormControlProps;
  labelProps?: FormLabelProps;
};

export const RHFTextFieldWithAdornment = <T extends FieldValues = Record<string, string>>({
  dataTest,
  label,
  name,
  control = undefined,
  formControlProps = { sx: { margin: 0 } },
  labelProps = undefined,
  startAdornment = undefined,
  endAdornment = undefined,
  slotProps,
  /** This meant to be used for manual error-state handling. If not set (undefined) the error state will be obtained from RHF. */
  error = undefined,
  /** If "true" - error state will only be shown when component was interacted with */
  errorsRequireInteraction = false,
  id = undefined,
  designSystem,
  ...props
}: RHFTextFieldWithAdornmentProps<T>): ReactElement => {
  const isDesignSystem = useDesignSystemEnabled('textField', designSystem);
  const {
    dirtyFields: { [name]: isDirty },
    errors: { [name]: isError },
    isSubmitting,
    touchedFields: { [name]: isTouched },
  } = useFormState({ name, ...(control ? { control } : {}) });

  const uid = useMemo<string>(() => id || name, [id, name]);
  const isDisabled = props.disabled || isSubmitting;
  const isErrorState = error ?? ((errorsRequireInteraction ? !!isDirty || !!isTouched : true) && !!isError);

  return (
    <FormControl disabled={isDisabled} fullWidth {...formControlProps} data-test={dataTest}>
      {/* The DS field renders its label as the label tab, so it gets the label itself. */}
      {label && !isDesignSystem && (
        <FormLabel {...labelProps} data-test={dataTest && `${dataTest}-label`} error={isErrorState} htmlFor={uid}>
          {label}
        </FormLabel>
      )}
      <RHFTextField
        id={uid}
        variant="outlined"
        {...props}
        {...(isDesignSystem && { label })}
        designSystem={isDesignSystem}
        error={isErrorState}
        control={control}
        slotProps={{
          ...slotProps,
          input: {
            ...(typeof slotProps?.input === 'object' ? slotProps.input : undefined),
            ...(startAdornment && {
              startAdornment: <InputAdornment position="start">{startAdornment}</InputAdornment>,
            }),
            ...(endAdornment && { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> }),
          },
          htmlInput: {
            ...(typeof slotProps?.htmlInput === 'object' ? slotProps.htmlInput : undefined),
          },
        }}
        name={name}
        dataTest={dataTest && `${dataTest}-input`}
      />
    </FormControl>
  );
};
